import type { ApolloCache } from '@apollo/client'

import { useMutation } from 'modules/core/hooks/useMutation'
import {
  ShiftManagementScheduleBulkDropDocument,
  ShiftsShiftFragmentDoc,
  ShiftsRestDayFragmentDoc,
  type ShiftManagementScheduleBulkDropMutation,
} from 'generated/resources/graphql'
import { groupBy } from 'lodash'
import type RestDay from 'modules/shift_management/store/internal/models/RestDay'
import type Shift from 'modules/shift_management/store/internal/models/Shift'

type ShiftRef = { _ref: string }

type BulkDropSchedule = NonNullable<
  ShiftManagementScheduleBulkDropMutation['shiftManagementMutations']['bulkDropShiftmanagementSchedule']['schedule']
>
type DroppedShift = BulkDropSchedule['shifts'][number]
type DroppedRestDay = BulkDropSchedule['restDays'][number]

type SchedulePasteItem = {
  sourceShifts: Shift[]
  sourceRestDays: RestDay[]
  targetRowId: string
  targetDate: string
  mode: 'copy' | 'move'
  removeTargetItems: boolean
  dropOverrides?: {
    startTime?: string | null
    locationId?: number | null
    workAreaId?: number | null
  }
}

type TargetDeleteItem = {
  targetShiftsToDelete: Shift[]
  targetRestDaysToDelete: RestDay[]
}

type BulkDropScheduleContext = {
  previousShifts: Shift[]
  previousRestDays: RestDay[]
  targetDeleteItems: TargetDeleteItem[]
}

const isBulkDropScheduleContext = (value: unknown): value is Partial<BulkDropScheduleContext> =>
  typeof value === 'object' && value !== null

type CacheShift = {
  workAreaId?: Shift['workAreaId']
  locationsWorkAreaId?: DroppedShift['locationsWorkAreaId']
}

type CacheRestDay = {
  employeeId?: RestDay['employeeId']
  employee?: DroppedRestDay['employee']
}

const normalizePositiveId = (value?: number | string | null) => {
  if (typeof value === 'number' && value > 0) return String(value)
  if (typeof value === 'string' && Number(value) > 0) return value

  return null
}

const getShiftWorkAreaId = (shift: CacheShift) => {
  const locationsWorkAreaId = normalizePositiveId(shift.locationsWorkAreaId)
  if (locationsWorkAreaId) return locationsWorkAreaId

  return normalizePositiveId(shift.workAreaId)
}

const getRestDayEmployeeId = (restDay: CacheRestDay) => {
  const employeeId = normalizePositiveId(restDay.employeeId)
  if (employeeId) return employeeId

  return normalizePositiveId(restDay.employee?.id)
}

const useBulkDropSchedule = () => {
  const removeShiftIdsFromEntity = ({
    cache,
    typename,
    entityId,
    idsToRemove,
  }: {
    cache: ApolloCache<unknown>
    typename: 'ShiftManagementEmployeesPermission' | 'ShiftManagementWorkArea'
    entityId: number
    idsToRemove: Set<string>
  }) => {
    if (idsToRemove.size === 0) return

    cache.modify({
      id: cache.identify({ __typename: typename, id: entityId }),
      fields: {
        shifts(existingShifts = [], { readField }) {
          return existingShifts.filter((item: ShiftRef) => {
            const id = readField('id', item)
            return typeof id !== 'string' || !idsToRemove.has(id)
          })
        },
      },
    })
  }

  const removeRestDayIdsFromEmployee = ({
    cache,
    employeeId,
    idsToRemove,
  }: {
    cache: ApolloCache<unknown>
    employeeId: number
    idsToRemove: Set<string>
  }) => {
    if (idsToRemove.size === 0) return

    cache.modify({
      id: cache.identify({
        __typename: 'ShiftManagementEmployeesPermission',
        id: employeeId,
      }),
      fields: {
        restDays(existingRestDays = [], { readField }) {
          return existingRestDays.filter((item: ShiftRef) => {
            const id = readField('id', item)
            if (typeof id === 'string' && idsToRemove.has(id)) {
              cache.evict({ id: cache.identify({ __typename: 'ShiftsRestDay', id }) })
              return false
            }

            return typeof id !== 'string' || !idsToRemove.has(id)
          })
        },
      },
    })
  }

  const appendShiftRefsToEntity = ({
    cache,
    typename,
    entityId,
    shiftsToAppend,
    refsById,
  }: {
    cache: ApolloCache<unknown>
    typename: 'ShiftManagementEmployeesPermission' | 'ShiftManagementWorkArea'
    entityId: number
    shiftsToAppend: Array<{ id: string }>
    refsById: Map<string, unknown>
  }) => {
    if (shiftsToAppend.length === 0) return

    cache.modify({
      id: cache.identify({ __typename: typename, id: entityId }),
      fields: {
        shifts(existingShifts = [], { readField }) {
          const existingIds = new Set<string>(
            existingShifts
              .map((item: ShiftRef) => readField('id', item))
              .filter((id: unknown): id is string => typeof id === 'string')
          )

          const refsToAdd = shiftsToAppend.flatMap((shift) => {
            if (existingIds.has(shift.id)) return []

            const shiftRef = refsById.get(shift.id)
            if (!shiftRef) return []

            existingIds.add(shift.id)
            return [shiftRef]
          })

          return refsToAdd.length > 0 ? [...existingShifts, ...refsToAdd] : existingShifts
        },
      },
    })
  }

  const appendRestDayRefsToEntity = ({
    cache,
    employeeId,
    restDaysToAppend,
    refsById,
  }: {
    cache: ApolloCache<unknown>
    employeeId: number
    restDaysToAppend: Array<{ id: string }>
    refsById: Map<string, unknown>
  }) => {
    if (restDaysToAppend.length === 0) return

    cache.modify({
      id: cache.identify({
        __typename: 'ShiftManagementEmployeesPermission',
        id: employeeId,
      }),
      fields: {
        restDays(existingRestDays = [], { readField }) {
          const existingIds = new Set<string>(
            existingRestDays
              .map((item: ShiftRef) => readField('id', item))
              .filter((id: unknown): id is string => typeof id === 'string')
          )

          const refsToAdd = restDaysToAppend.flatMap((restDay) => {
            if (existingIds.has(restDay.id)) return []

            const restDayRef = refsById.get(restDay.id)
            if (!restDayRef) return []

            existingIds.add(restDay.id)
            return [restDayRef]
          })

          return refsToAdd.length > 0 ? [...existingRestDays, ...refsToAdd] : existingRestDays
        },
      },
    })
  }

  const removeShiftsFromCache = ({
    cache,
    shiftsRaw,
  }: {
    cache: ApolloCache<unknown>
    shiftsRaw: unknown
  }) => {
    const shifts = Array.isArray(shiftsRaw) ? shiftsRaw : []

    const shiftsByEmployee = groupBy(shifts, 'employeeId')
    Object.entries(shiftsByEmployee).forEach(([employeeId, employeeShifts]) => {
      const idsToRemove = new Set(employeeShifts.map((shift: { id: string }) => shift.id))

      removeShiftIdsFromEntity({
        cache,
        typename: 'ShiftManagementEmployeesPermission',
        entityId: Number(employeeId),
        idsToRemove,
      })
    })

    const shiftsByWorkArea = groupBy(
      shifts.filter((shift) => getShiftWorkAreaId(shift) !== null),
      (shift) => getShiftWorkAreaId(shift)
    )

    Object.entries(shiftsByWorkArea).forEach(([workAreaId, shiftsInWorkArea]) => {
      const numericWorkAreaId = Number(workAreaId)
      if (!numericWorkAreaId) return

      const idsToRemove = new Set(shiftsInWorkArea.map((shift: { id: string }) => shift.id))

      removeShiftIdsFromEntity({
        cache,
        typename: 'ShiftManagementWorkArea',
        entityId: numericWorkAreaId,
        idsToRemove,
      })
    })
  }

  const removeRestDaysFromCache = ({
    cache,
    restDaysRaw,
  }: {
    cache: ApolloCache<unknown>
    restDaysRaw: unknown
  }) => {
    const restDays = Array.isArray(restDaysRaw) ? restDaysRaw : []
    const restDaysByEmployee = groupBy(restDays, getRestDayEmployeeId)

    Object.entries(restDaysByEmployee).forEach(([employeeId, employeeRestDays]) => {
      const numericEmployeeId = Number(employeeId)
      if (!numericEmployeeId) return

      const idsToRemove = new Set(employeeRestDays.map((restDay: { id: string }) => restDay.id))

      removeRestDayIdsFromEmployee({
        cache,
        employeeId: numericEmployeeId,
        idsToRemove,
      })
    })
  }

  const handleRemoveTargetItems = ({
    cache,
    targetDeleteItemsRaw,
  }: {
    cache: ApolloCache<unknown>
    targetDeleteItemsRaw: unknown
  }) => {
    const targetDeleteItems = Array.isArray(targetDeleteItemsRaw) ? targetDeleteItemsRaw : []

    targetDeleteItems.forEach((targetDeleteItem) => {
      if (
        typeof targetDeleteItem === 'object' &&
        targetDeleteItem !== null &&
        'targetShiftsToDelete' in targetDeleteItem
      ) {
        removeShiftsFromCache({
          cache,
          shiftsRaw: targetDeleteItem.targetShiftsToDelete,
        })
      }

      if (
        typeof targetDeleteItem === 'object' &&
        targetDeleteItem !== null &&
        'targetRestDaysToDelete' in targetDeleteItem
      ) {
        removeRestDaysFromCache({
          cache,
          restDaysRaw: targetDeleteItem.targetRestDaysToDelete,
        })
      }
    })
  }

  const [bulkDropSchedule] = useMutation(ShiftManagementScheduleBulkDropDocument, {
    extractorFn: (data) => data.shiftManagementMutations.bulkDropShiftmanagementSchedule,
    update: (cache, { data }, options) => {
      const context = isBulkDropScheduleContext(options.context) ? options.context : null

      if (context?.previousShifts) {
        removeShiftsFromCache({
          cache,
          shiftsRaw: context.previousShifts,
        })
      }

      if (context?.previousRestDays) {
        removeRestDaysFromCache({
          cache,
          restDaysRaw: context.previousRestDays,
        })
      }

      if (context?.targetDeleteItems) {
        handleRemoveTargetItems({
          cache,
          targetDeleteItemsRaw: context.targetDeleteItems,
        })
      }

      const schedule = data?.shiftManagementMutations.bulkDropShiftmanagementSchedule.schedule
      if (!schedule) return

      const droppedShifts = schedule.shifts ?? []
      const droppedRestDays = schedule.restDays ?? []

      const droppedShiftRefsById = new Map<string, unknown>()
      droppedShifts.forEach((droppedShift) => {
        const newRef = cache.writeFragment({
          data: droppedShift,
          fragment: ShiftsShiftFragmentDoc,
          fragmentName: 'ShiftsShift',
        })

        if (newRef) droppedShiftRefsById.set(droppedShift.id, newRef)
      })

      const droppedShiftsByEmployee = groupBy(droppedShifts, 'employeeId')
      Object.entries(droppedShiftsByEmployee).forEach(([employeeId, employeeDroppedShifts]) => {
        appendShiftRefsToEntity({
          cache,
          typename: 'ShiftManagementEmployeesPermission',
          entityId: Number(employeeId),
          shiftsToAppend: employeeDroppedShifts,
          refsById: droppedShiftRefsById,
        })
      })

      const droppedShiftsByWorkArea = groupBy(
        droppedShifts.filter((shift) => getShiftWorkAreaId(shift) !== null),
        (shift) => getShiftWorkAreaId(shift)
      )
      Object.entries(droppedShiftsByWorkArea).forEach(([workAreaId, workAreaDroppedShifts]) => {
        const numericWorkAreaId = Number(workAreaId)
        if (!numericWorkAreaId) return

        appendShiftRefsToEntity({
          cache,
          typename: 'ShiftManagementWorkArea',
          entityId: numericWorkAreaId,
          shiftsToAppend: workAreaDroppedShifts,
          refsById: droppedShiftRefsById,
        })
      })

      const droppedRestDayRefsById = new Map<string, unknown>()
      droppedRestDays.forEach((droppedRestDay) => {
        const newRef = cache.writeFragment({
          data: droppedRestDay,
          fragment: ShiftsRestDayFragmentDoc,
          fragmentName: 'ShiftsRestDay',
        })

        if (newRef) droppedRestDayRefsById.set(droppedRestDay.id, newRef)
      })

      const droppedRestDaysByEmployee = groupBy(droppedRestDays, (restDay: DroppedRestDay) =>
        getRestDayEmployeeId(restDay)
      )
      Object.entries(droppedRestDaysByEmployee).forEach(([employeeId, employeeDroppedRestDays]) => {
        const numericEmployeeId = Number(employeeId)
        if (!numericEmployeeId) return

        appendRestDayRefsToEntity({
          cache,
          employeeId: numericEmployeeId,
          restDaysToAppend: employeeDroppedRestDays,
          refsById: droppedRestDayRefsById,
        })
      })
    },
  })

  return bulkDropSchedule
}

export { useBulkDropSchedule }
export type { SchedulePasteItem, TargetDeleteItem }
